import moment from 'moment'

import type {
  CaptureRule,
  ExclusionFilterOption,
  IndexedCaptureRule,
} from '../types'

export const formatExclusionDate = (
  value?: string | number
): string => {
  if (!value) return '-'
  const date = moment(value)
  return date.isValid() ? date.format('YYYY-MM-DD HH:mm') : String(value)
}

export const createExclusionFilterOptions = (
  rules: CaptureRule[]
): ExclusionFilterOption[] =>
  Array.from(
    new Set(
      rules
        .map(rule => rule.value)
        .filter((value): value is string => Boolean(value))
    )
  ).map(value => ({ key: value, label: value }))

const parseClientLabel = (
  label: string
): Pick<ExclusionFilterOption, 'number' | 'name'> => {
  const match = label.match(/^(\d+)\s+(.+)$/)
  if (match) return { number: match[1], name: match[2] }
  return { name: label }
}

export const createBlockBillingFilterOptions = (
  rules: CaptureRule[]
): ExclusionFilterOption[] =>
  Array.from(
    new Set(
      rules
        .map(rule => rule.value)
        .filter((value): value is string => Boolean(value))
    )
  ).map(value => ({ key: value, label: value, ...parseClientLabel(value) }))

export const createExclusionGridData = (
  rules: CaptureRule[],
  filter: string
): IndexedCaptureRule[] =>
  rules
    .filter(rule => (filter ? rule.value === filter : true))
    .slice()
    .sort((first, second) => {
      const firstTime = new Date(first.updated_on || first.id).getTime()
      const secondTime = new Date(second.updated_on || second.id).getTime()
      return secondTime - firstTime
    })
    .map((rule, index) => ({ ...rule, index: index + 1 }))

export const normalizeServiceExclusionRules = (
  exclusions: unknown[]
): CaptureRule[] =>
  exclusions
    .map((item, index): CaptureRule | null => {
      const record =
        item && typeof item === 'object'
          ? (item as Record<string, unknown>)
          : null
      const value =
        typeof item === 'string'
          ? item
          : String(
              record?.value ??
                record?.website ??
                record?.url ??
                record?.domain ??
                ''
            )

      if (!value) return null
      return {
        id: record?.id
          ? String(record.id)
          : `service-exclusion-${index}-${value}`,
        readOnly: true,
        updated_on:
          typeof record?.updated_on === 'string'
            ? record.updated_on
            : undefined,
        value,
      }
    })
    .filter((rule): rule is CaptureRule => rule !== null)
